import Vue from 'vue'
import moment from 'moment'
import InspectorStore from '../InspectorStore'
import * as PlotConfigStore from './PlotConfigStore'
import preparePlotData from './prepare-plot-data'

PlotConfigStore.init()

export default new Vue({
  data() {
    return {
      strPlotConfigs: PlotConfigStore.getStr(),
      allPlots: PlotConfigStore.findAll(),

      plotLineWidth: 400,
      plotLineHeight: 30,

      xScale: null,
      yScales: {},
      lineFuncs: {},
      plotPaths: {},
      dataRows: [],

      zoom: null
    }
  },
  computed: {
    pinnedPlots() {
      return this.allPlots.filter((x) => x.isPinned)
    },

    otherPlots() {
      return this.allPlots.filter((x) => !x.isPinned)
    }
  },
  methods: {
    parsePlotConfigs() {
      PlotConfigStore.setStr(this.strPlotConfigs)
      this.allPlots = PlotConfigStore.findAll()
      this.dataRows = preparePlotData({
        frames: InspectorStore.allFrames,
        allPlots: this.allPlots
      })
    },

    togglePin(plotConfig) {
      PlotConfigStore.togglePin(plotConfig)
      this.strPlotConfigs = PlotConfigStore.getStr()
    },

    remove(plotConfig) {
      PlotConfigStore.remove(plotConfig)
      this.strPlotConfigs = PlotConfigStore.getStr()
      this.allPlots = PlotConfigStore.findAll()
    },

    fillPlotConfigs() {
      const res = PlotConfigStore.fillMissingPlots(InspectorStore.allFrames)
      this.strPlotConfigs = PlotConfigStore.getStr()
      this.allPlots = PlotConfigStore.findAll()
      this.dataRows = preparePlotData({
        frames: InspectorStore.allFrames,
        allPlots: this.allPlots
      })

      return res
    },

    computeCaret(mouseX) {
      // console.log('computing caret')

      if (mouseX == null || this.xScale == null) {
        this.caret = null
        return
      }

      const timestamp = this.xScale.invert(mouseX)
      // eslint-disable-next-line no-undef
      const bisectDate = d3.bisector((d) => d.timestamp).left
      const index = bisectDate(this.dataRows, timestamp)
      const dataRow = this.dataRows[index]

      // Calcula o X do caret, pois a largura e a scale de
      // todos os plots são os mesmos.
      const caretX = this.xScale(timestamp)

      // Calcula o valor de cada PGN para mostrar
      // const plotValues = {}
      for (let plotConfig of this.allPlots) {
        // const val = plotConfig.getter(dataRow)
        // plotValues[plotConfig.id] = val
        const val = dataRow && dataRow.values[plotConfig.id]

        const text = val == null ? '' : `${val} (0x${val.toString(16)})`
        // eslint-disable-next-line no-undef
        d3.select(`#plot_${plotConfig.id} .caret-value`).text(text)
      }

      // this.caret = {
      //   timestamp,
      //   index,
      //   dataRow,
      //   caretX,
      //   plotValues
      // }

      // eslint-disable-next-line no-undef
      d3.selectAll('svg .caret').attr('transform', `translate(${caretX}, 0)`)
      // eslint-disable-next-line no-undef
      d3.selectAll('.caret-timestamp').text(
        moment(timestamp).format('YYYY-MM-DD HH:mm:ss Z')
      )
    },

    prepareD3() {
      const data = this.dataRows
      const width = this.plotLineWidth
      const height = this.plotLineHeight
      const xScale = computeXScale({ data, width })
      this.xScale = xScale

      this.yScales = {}
      this.lineFuncs = {}
      for (let plotConfig of this.allPlots) {
        const yScale = computeYScale({ data, height, plotConfig })
        this.yScales[plotConfig.id] = yScale

        const { lineFunc, areaFunc } = computeLineFuncs({
          xScale,
          yScale,
          plotConfig
        })
        this.lineFuncs[plotConfig.id] = { lineFunc, areaFunc }
      }

      // Prepara um zoomBehavior que afeta a xScale.
      // eslint-disable-next-line no-undef
      this.zoomBehavior = d3.behavior.zoom()
      this.zoomBehavior.scaleExtent([1, 10000]) // max zoom
      this.zoomBehavior.x(this.xScale)

      // Quando ocorre zoom, recalcula todos os paths.
      this.zoomBehavior.on('zoom', () => {
        // console.log('zooming')
        // acho que isto não precisa ser feito, isto é pra
        // controlar zoom programaticamente (era usado antes
        // para que o zoom em uma line afetasse o zoom de
        // outras, mas agora é tudo o mesmo zoomBehavior)
        //
        // const { translate, scale } = d3.event
        // this.zoomBehavior.translate(translate)
        // this.zoomBehavior.scale(scale)
        this.redraw()
      })

      // Observe que ainda não plugamos os listeners do zoom; isto
      // é feito por cada PlotLine.
    },

    // plugZoomListener(el) {
    //   // eslint-disable-next-line no-undef
    //   d3.select(el).call(this.zoomBehavior)
    // },
    plugZoomListeners() {
      for (let plotConfig of this.allPlots) {
        const sel = `#plot_${plotConfig.id} svg`
        // eslint-disable-next-line no-undef
        d3.select(sel).call(this.zoomBehavior)
      }
    },

    redraw() {
      // console.log('redrawing', this.xScale.range())

      for (let plotConfig of this.allPlots) {
        const id = plotConfig.id
        const strokePathD = this.lineFuncs[id].lineFunc(this.dataRows)
        const fillPathD = this.lineFuncs[id].areaFunc(this.dataRows)
        // this.plotPaths[id] = { strokePathD, fillPathD }
        this.$set(this.plotPaths, id, { strokePathD, fillPathD })

        // eslint-disable-next-line no-undef
        d3.select(`#plot_${id} path.line`).attr('d', strokePathD)
        // eslint-disable-next-line no-undef
        d3.select(`#plot_${id} path.area`).attr('d', fillPathD)
      }
    }
  }
})

function computeXScale({ data, width }) {
  // eslint-disable-next-line no-undef
  const xExtent = d3.extent(data, (d) => d.timestamp)

  // eslint-disable-next-line no-undef
  const xScale = d3.scale
    .linear()
    .domain(xExtent)
    .range([0, width])

  return xScale
}

function computeYScale({ data, height, plotConfig }) {
  // eslint-disable-next-line no-undef
  // o minY sempre será 0
  // const yExtent = [0, d3.max(data, (d) => plotConfig.getter(d))]
  // eslint-disable-next-line no-undef
  const yExtent = [0, d3.max(data, (d) => d.values[plotConfig.id])]

  // eslint-disable-next-line no-undef
  const yScale = d3.scale
    .linear()
    .domain(yExtent)
    .range([height, 0])

  return yScale
}

function computeLineFuncs({ xScale, yScale, plotConfig }) {
  // eslint-disable-next-line no-undef
  const lineFunc = d3.svg
    .line()
    .x((d) => xScale(d.timestamp))
    // .y((d) => yScale(plotConfig.getter(d)))
    .y((d) => yScale(d.values[plotConfig.id]))

  // eslint-disable-next-line no-undef
  const areaFunc = d3.svg
    .area()
    .x((d) => xScale(d.timestamp))
    .y0(yScale(0))
    .y1((d) => yScale(d.values[plotConfig.id]))

  return { lineFunc, areaFunc }
}
